import { useMemo } from 'react'
import {
  type DashTablePaginationConfigurationProps,
  type DashTableManualPaginationProps,
  type DashTableBaseFiltersType,
  type DashTableState,
} from './dashTable.types'

export type UseDashTablePaginationConfigurationProps<TFilters> = Pick<
  DashTablePaginationConfigurationProps,
  'initialPageSize' | 'hidePageSizeSelector'
> &
  Pick<DashTableManualPaginationProps<TFilters>, 'manualPagination'> & {
    inputRecordCount: DashTableManualPaginationProps<TFilters>['totalRecordCount']
    dataLength: number
    state: DashTableState
  }

export const useDashTablePaginationConfiguration = <TFilters extends DashTableBaseFiltersType>({
  initialPageSize,
  hidePageSizeSelector,
  manualPagination,
  inputRecordCount,
  dataLength,
  state: { pageIndex, pageSize },
}: UseDashTablePaginationConfigurationProps<TFilters>): DashTablePaginationConfigurationProps => {
  const firstExpectedVisibleRecordIndex = pageSize * pageIndex
  const lastExpectedRecordIndex = pageSize * (pageIndex + 1) - 1

  const totalRecordCount = useMemo(() => {
    if (!manualPagination) {
      return dataLength
    }

    if (inputRecordCount === undefined) {
      const lastVisibleRecordIndex = firstExpectedVisibleRecordIndex + dataLength

      // If we don't have a full page of data, we can infer that we have hit the end of pagination
      if (lastVisibleRecordIndex < lastExpectedRecordIndex) {
        return lastVisibleRecordIndex
      }
    }

    return inputRecordCount
  }, [
    manualPagination,
    inputRecordCount,
    dataLength,
    firstExpectedVisibleRecordIndex,
    lastExpectedRecordIndex,
  ])

  const firstVisibleRecordIndex = useMemo(() => {
    if (totalRecordCount === 0) return 0

    return firstExpectedVisibleRecordIndex
  }, [firstExpectedVisibleRecordIndex, totalRecordCount])

  const lastVisibleRecordIndex = useMemo(() => {
    if (totalRecordCount === 0) return 0
    if (totalRecordCount === undefined) return lastExpectedRecordIndex

    return Math.min(lastExpectedRecordIndex, totalRecordCount - 1)
  }, [lastExpectedRecordIndex, totalRecordCount])

  return {
    initialPageSize,
    hidePageSizeSelector,
    totalRecordCount,
    firstVisibleRecordIndex,
    lastVisibleRecordIndex,
  }
}
